import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Lock, Mail, Eye, EyeOff, AlertCircle, GraduationCap } from 'lucide-react';
import logoImage from '@/images/meenammalogobg.png';

const ROLE_LABELS = {
  admin:    { label: 'Administrator',      color: 'text-purple-600',  bg: 'bg-purple-50 border-purple-200' },
  accounts: { label: 'Accounts Manager',   color: 'text-blue-600',    bg: 'bg-blue-50 border-blue-200'   },
  student:  { label: 'Student Services',   color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' },
  payments: { label: 'Payments Officer',   color: 'text-amber-600',   bg: 'bg-amber-50 border-amber-200' },
};

const AdminLogin = ({ onLogin }) => {
  const [form, setForm]         = useState({ email: '', password: '' });
  const [showPwd, setShowPwd]   = useState(false);
  const [error, setError]       = useState('');
  const [isLoading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { setError('Email is required'); return; }
    if (!form.password)     { setError('Password is required'); return; }

    setLoading(true);
    setError('');

    try {
      const res  = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email.trim(), password: form.password }),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.error || 'Login failed');

      localStorage.setItem('admin_token', data.token);
      localStorage.setItem('admin_user',  JSON.stringify(data.user));
      onLogin({ token: data.token, user: data.user });

    } catch (err) {
      setError(err.message || 'Unable to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4 relative overflow-hidden">
      {/* Background blobs */}
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-brand-indigo/6 blur-[100px] pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-brand-emerald/6 blur-[100px] pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y:  0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="bg-white rounded-3xl shadow-2xl shadow-slate-200/60 border border-slate-100 overflow-hidden">

          {/* Header band */}
          <div className="bg-gradient-to-r from-brand-indigo via-brand-indigo to-brand-emerald p-6 pb-8 text-center">
            <div className="flex justify-center mb-3">
              <div className="bg-white/20 rounded-2xl p-3 backdrop-blur-sm">
                <img src={logoImage} alt="Meenamma" className="h-12 w-auto drop-shadow" />
              </div>
            </div>
            <h1 className="text-white text-xl font-bold tracking-tight">Meenamma Educational Trust</h1>
            <p className="text-white/70 text-sm mt-1">Staff Portal — Sign In</p>
          </div>

          {/* Form area */}
          <div className="p-8 -mt-4 bg-white rounded-t-3xl">

            {/* Role hint cards */}
            <div className="grid grid-cols-2 gap-2 mb-6">
              {Object.entries(ROLE_LABELS).map(([role, cfg]) => (
                <div key={role} className={`text-center text-xs py-1.5 px-2 rounded-lg border ${cfg.bg} ${cfg.color} font-medium`}>
                  {cfg.label}
                </div>
              ))}
            </div>

            {/* Error */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                className="mb-5 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5"
              >
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@meenamma.org"
                    required
                    autoFocus
                    disabled={isLoading}
                    className="w-full pl-10 pr-4 py-2.5 border border-border rounded-xl text-sm bg-background
                               focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50
                               disabled:opacity-50 transition-all"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type={showPwd ? 'text' : 'password'}
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    disabled={isLoading}
                    className="w-full pl-10 pr-10 py-2.5 border border-border rounded-xl text-sm bg-background
                               focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50
                               disabled:opacity-50 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 bg-gradient-to-r from-brand-indigo to-brand-emerald text-white
                           font-semibold rounded-xl shadow-lg shadow-primary/20 hover:shadow-primary/30
                           hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200 border-0 mt-2"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2 justify-center">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Signing in…
                  </span>
                ) : 'Sign In'}
              </Button>
            </form>

            <p className="text-center text-xs text-muted-foreground mt-5">
              Authorized personnel only &mdash; access is logged
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
